/**
 * Identité visuelle des lignes.
 *
 * Ce sont les **couleurs réelles de l'app**, celles du COJOJ : sur la carte de
 * l'accrédité, YOG 1 est violette et YOG 2 bleue. Un graphique du back-office qui les recolorerait
 * obligerait l'opérateur à traduire mentalement entre son écran et le téléphone
 * qu'il a en main au téléphone du TROC.
 *
 * On passe par les variables CSS (`--c-blue`…) et jamais par le code
 * hexadécimal : c'est ce qui fait suivre le thème sombre.
 */

export interface LineIdentity {
  lineNo: string;
  name: string;
  /** Expression CSS, pas un hexadécimal. */
  color: string;
  soft: string;
}

const yog = (n: string): LineIdentity => ({
  lineNo: `YOG-${n}`,
  name: `YOG ${n}`,
  color: `var(--c-yog-${n.toLowerCase()})`,
  soft: `var(--c-yog-${n.toLowerCase()}-soft)`,
});

/**
 * Les douze navettes, dans l'ordre d'affichage, chacune dans sa couleur officielle
 * (COJOJ, 2026-09-28 ; variables `--c-yog-*` de `styles.css`) — YOG 1-5 à Dakar,
 * 6-9 à Diamniadio, 10-11 à Saly.
 */
const ORDERED: LineIdentity[] = [
  yog('1'),
  yog('2'),
  yog('3'),
  yog('4A'),
  yog('4B'),
  yog('5'),
  yog('6'),
  yog('7'),
  yog('8'),
  yog('9'),
  yog('10'),
  yog('11'),
];

const KNOWN: Record<string, LineIdentity> = Object.fromEntries(ORDERED.map((line) => [line.lineNo, line]));

/** Repli pour une ligne que Lenz exposerait sans qu'on la connaisse encore. */
const FALLBACK_COLORS = ['var(--s-4)', 'var(--s-6)', 'var(--s-5)'];

export function lineIdentity(lineNo: string, index = 0): LineIdentity {
  const known = KNOWN[lineNo];
  if (known) return known;
  const color = FALLBACK_COLORS[index % FALLBACK_COLORS.length] ?? 'var(--s-4)';
  return { lineNo, name: lineNo, color, soft: 'var(--c-surface-alt)' };
}

/** Ordre d'affichage stable : les navettes olympiques d'abord, le test ensuite. */
export function sortLineNos(lineNos: string[]): string[] {
  // Rang de la table, pas un tri de chaînes : « YOG-10 » passerait avant « YOG-2 ».
  const rank = (n: string): number => {
    const index = ORDERED.findIndex((line) => line.lineNo === n);
    return index === -1 ? ORDERED.length : index;
  };
  return [...lineNos].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, 'fr'));
}

/** Palette générique quand la dimension n'est pas « ligne » (plateforme, langue…). */
export const SERIES_COLORS = [
  'var(--s-1)',
  'var(--s-2)',
  'var(--s-3)',
  'var(--s-4)',
  'var(--s-5)',
  'var(--s-6)',
];

export function seriesColor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length] ?? 'var(--s-1)';
}
